import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import * as React from "react";

import {
  SKILL_DESCRIPTION_MAX_LENGTH,
  SkillAccessibility,
  SkillDraft,
  validateSkillName,
} from "@/shared/domain/skills";
import {
  useCreateSkill,
  useKnownSkillTags,
} from "@/shared/domain/skills/hooks";
import { Button } from "@/shared/foundation/primitives";

import { useRelevantTopics } from "./relevantTopics";
import styles from "./SkillEditor.module.css";
import { TagsAutocomplete } from "./TagsAutocomplete";
import { TopicBodyEditor } from "./TopicBodyEditor";

/** Trim, drop empties, and dedupe (case-insensitive on the original casing). */
function normalizeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of tags) {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Seed values, e.g. a draft handed over from the `client_create_skill` chat
   * tool. Read once when the dialog (re)opens; the user edits freely from
   * there.
   */
  initialValues?: SkillDraft | null;
  orgId: string | undefined;
}

interface CreateFormState {
  // Skill-level fields: a create makes the skill row and version 1 atomically.
  name: string;
  accessibility: SkillAccessibility;
  tags: string[];
  // Version 1's fields.
  description: string;
  body: string;
}

const EMPTY_CREATE: CreateFormState = {
  name: "",
  accessibility: "private",
  tags: [],
  description: "",
  body: "",
};

/**
 * Seed the create form from an optional draft, filling every field the draft
 * leaves out with the empty-form default. A null / absent draft yields the
 * plain empty form, so the manual "Create Skill" button and the chat-tool
 * handoff share one code path.
 */
function seedCreateForm(draft: SkillDraft | null | undefined): CreateFormState {
  if (!draft) {
    return EMPTY_CREATE;
  }
  return {
    name: draft.name ?? EMPTY_CREATE.name,
    accessibility: draft.accessibility ?? EMPTY_CREATE.accessibility,
    tags: draft.tags ?? EMPTY_CREATE.tags,
    description: draft.description ?? EMPTY_CREATE.description,
    body: draft.body ?? EMPTY_CREATE.body,
  };
}

/**
 * Dialog for creating a skill — its row and its first version, atomically.
 *
 * Create only. Editing an existing version happens on the skill's own page
 * (`SkillDetail`), which every affordance that used to open this dialog in edit
 * mode now links to; the skill's own name, accessibility, and tags are edited
 * in place in that page's header.
 */
export function SkillEditor({ open, onClose, initialValues, orgId }: Props) {
  const [createForm, setCreateForm] =
    React.useState<CreateFormState>(EMPTY_CREATE);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const knownTagsQuery = useKnownSkillTags(orgId);
  const { topicNames, isCatalogLoading, relevantTopicsFor } =
    useRelevantTopics(orgId);
  const createSkill = useCreateSkill(orgId);

  // Reseed only when the dialog is (re)opened. ``initialValues`` is excluded
  // from the deps: it's a handoff seed read once at open-time, so reseeding
  // mid-edit if the caller's reference changed would clobber the user's edits.
  React.useEffect(() => {
    if (!open) {
      return;
    }
    setCreateForm(seedCreateForm(initialValues));
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: see comment above
  }, [open]);

  function updateCreate<K extends keyof CreateFormState>(
    key: K,
    value: CreateFormState[K],
  ) {
    setCreateForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setErrorMessage(null);
    const trimmedName = createForm.name.trim();
    const nameError = validateSkillName(trimmedName);
    if (nameError !== null) {
      setErrorMessage(nameError);
      return;
    }
    // TagsAutocomplete commits pending text on blur — by the time this
    // handler runs (after the Save button's click), any typed-but-not-
    // Enter'd tag is already in createForm.tags. Just dedupe.
    const initialTags = normalizeTags(createForm.tags);
    try {
      await createSkill.mutateAsync({
        name: trimmedName,
        accessibility: createForm.accessibility,
        description: createForm.description,
        body: createForm.body,
        tags: initialTags.length > 0 ? initialTags : undefined,
        relevant_topics: relevantTopicsFor(createForm.body),
      });
      onClose();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to save.");
    }
  }

  // Hold Save while the topic catalog is still loading so relevant_topics is
  // extracted against the real catalog, not an empty one (which would drop
  // references on create). If the query errored, isCatalogLoading is false and
  // relevantTopicsFor falls back to undefined, so the user can still save
  // without wiping stored topics.
  const isSaving = createSkill.isPending || isCatalogLoading;

  const liveCreateNameError = (() => {
    const trimmed = createForm.name.trim();
    return trimmed.length === 0 ? null : validateSkillName(trimmed);
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: styles.dialogPaper }}
    >
      <DialogTitle>Create Skill</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <div className={styles.formStack}>
          <TextField
            label="Name"
            value={createForm.name}
            onChange={(e) => updateCreate("name", e.target.value)}
            error={liveCreateNameError !== null}
            helperText={
              liveCreateNameError ??
              "Letters, digits, hyphens, and underscores. No spaces."
            }
            required
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="accessibility-label">Accessibility</InputLabel>
            <Select
              labelId="accessibility-label"
              label="Accessibility"
              value={createForm.accessibility}
              onChange={(e) =>
                updateCreate(
                  "accessibility",
                  e.target.value as SkillAccessibility,
                )
              }
            >
              <MenuItem value="private">Private (only you)</MenuItem>
              <MenuItem value="org">
                Org (everyone in your org can use it)
              </MenuItem>
              <MenuItem value="org-editable">
                Org-editable (everyone in your org can use and edit it)
              </MenuItem>
            </Select>
          </FormControl>
          <TagsAutocomplete
            value={createForm.tags}
            onChange={(next) => updateCreate("tags", next)}
            options={knownTagsQuery.data ?? []}
            helperText="Free-form labels for grouping skills (e.g. qa-review, experiments). Press Enter or click out to add."
          />
          <TextField
            label="When to use"
            value={createForm.description}
            onChange={(e) => updateCreate("description", e.target.value)}
            helperText={`Short description the AI uses to decide when to apply this skill (≤${SKILL_DESCRIPTION_MAX_LENGTH} chars).`}
            multiline
            minRows={2}
            inputProps={{ maxLength: SKILL_DESCRIPTION_MAX_LENGTH }}
            required
            fullWidth
          />
          <TopicBodyEditor
            label="Procedure / body"
            value={createForm.body}
            onChange={(next) => updateCreate("body", next)}
            topicNames={topicNames}
            orgId={orgId}
            helperText="Markdown supported. Type “[[” to reference a topic — referenced topics are pre-loaded for the AI."
            inputClassName={styles.bodyInput}
            grow
            required
          />
          {errorMessage && (
            <small className={styles.errorMessage}>{errorMessage}</small>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSave();
          }}
          disabled={isSaving || liveCreateNameError !== null}
          loading={createSkill.isPending}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
